<template>
  <div class="test-room">
    <AppBreadcrumb :items="breadcrumbItems" :showBack="true" @back="goBack">
      <template #actions>
        <div class="header-actions">
          <a-tag color="blue">
            {{ currentIndex + 1 }} / {{ questions.length }}
          </a-tag>

          <a-tag color="green">
            Javob berilgan: {{ answeredCount }} / {{ questions.length }}
          </a-tag>

          <!-- Xato savollar testi uchun maxsus ma'lumot -->
          <a-tag v-if="isWrongMode" color="orange">
            Qolgan xato: {{ remainingWrongCount }}
          </a-tag>

          <a-button
            type="primary"
            danger
            :loading="finishLoading"
            :disabled="isFinished"
            @click="finishTest"
          >
            Yakunlash
          </a-button>
        </div>
      </template>
    </AppBreadcrumb>

    <template v-if="questions.length">
      <a-row :gutter="[14, 14]">
        <a-col :xs="24" :xl="12">
          <a-card class="custom-card panel-card">
            <template #title>
              <div class="title-row">
                <div>
                  <div class="test-title">{{ title }}</div>
                  <div class="test-subtitle">
                    Savol {{ currentIndex + 1 }} / {{ questions.length }}
                  </div>
                </div>
                <!-- Xato savollar testi uchun badge -->
                <a-tag v-if="isWrongMode" color="orange">Xato savol</a-tag>
                <a-tag v-else color="blue">F1-F6</a-tag>
              </div>
            </template>

            <h2 class="question-title">
              {{ currentQuestion.text || 'Savol topilmadi' }}
            </h2>

            <!-- Xato savollar uchun oldingi xato ma'lumotlari -->
            <div v-if="isWrongMode && currentQuestion.previous_wrong_answer" class="wrong-info">
              <a-alert
                type="error"
                :message="'Siz oldin xato belgilagansiz: ' + currentQuestion.previous_wrong_answer.text"
                show-icon
                class="wrong-alert"
              />
            </div>

            <div class="options">
              <a-button
                v-for="(option, index) in currentQuestion.options || []"
                :key="option.id"
                block
                class="option-btn"
                :class="getOptionClass(option)"
                :disabled="finishLoading || isFinished"
                @click="selectAnswer(option)"
              >
                <span class="option-hotkey">F{{ index + 1 }}</span>
                <span class="option-text">{{ option.text }}</span>
              </a-button>
            </div>
          </a-card>
        </a-col>

        <a-col :xs="24" :xl="12">
          <a-card class="custom-card panel-card">
            <div class="image-wrap">
              <img
                v-if="currentQuestion.image_url"
                :src="currentQuestion.image_url"
                class="question-image"
                alt="Savol rasmi"
              />
              <div v-else class="no-image">
                <img style="width: 100%; height: 100%;" src="../../public/no-image.png" />
              </div>
            </div>

            <div v-if="showExplanation" class="explanation">
              <div class="explanation-title">{{ currentQuestion.explanation || 'Tushuntirish mavjud emas' }}</div>

              <a-alert
                v-if="isCurrentAnswerCorrect"
                type="success"
                message="To‘g‘ri javob belgilandi"
                show-icon
              />

              <a-alert
                v-else-if="isCurrentAnswerWrong"
                type="error"
                message="Xato javob belgilandi"
                show-icon
              />
            </div>
          </a-card>
        </a-col>
      </a-row>

      <div class="fixed-bottom-bar">
        <div class="question-number-grid">
          <button
            v-for="(q, index) in questions"
            :key="q.id"
            class="question-btn"
            :class="getQuestionBtnClass(index)"
            @click="goToQuestion(index)"
          >
            {{ index + 1 }}
          </button>
        </div>
      </div>
    </template>

    <template v-else>
      <a-empty description="Savollar topilmadi" />
    </template>

    
  </div>
</template>

<script>
import axios from 'axios'
import AppBreadcrumb from './AppBreadcrumb.vue'

export default {
  name: 'TicketTestRoom',

  components: {
    AppBreadcrumb
  },

  props: {
    title: { type: String, default: '' },
    questions: { type: Array, default: () => [] },
    resultId: { type: Number, required: true },
    backRoute: { type: String, default: '/' },
    pageTitle: { type: String, default: 'Biletlar' },
    ticketType: { type: Number, default: null },
    ticketNumber: { type: Number, default: null },
    // Yangi props: test rejimi
    mode: { type: String, default: 'ticket' }, // 'ticket' yoki 'wrong'
    wrongQuestionIds: { type: Array, default: () => [] }
  },

  emits: ['back', 'go-list', 'finished'],

  data() {
    return {
      backendUrl: 'https://avtotesting.uz/api',
      currentIndex: 0,
      selectedAnswers: {},
      showExplanation: false,
      finishLoading: false,
      isFinished: false,
      errorMessage: '',
      startedAt: Date.now(),
      // Xato savollar uchun qo'shimcha
      resolvedWrongQuestions: []
    }
  },

  computed: {
    // Test rejimi xato savollar uchunmi?
    isWrongMode() {
      return this.mode === 'wrong'
    },

    currentQuestion() {
      return this.questions?.[this.currentIndex] || {}
    },

    selectedOptionId() {
      return this.selectedAnswers[this.currentQuestion?.id] || null
    },

    answeredCount() {
      return Object.keys(this.selectedAnswers).length
    },

    allAnswered() {
      return this.questions.length > 0 && this.answeredCount === this.questions.length
    },

    breadcrumbItems() {
      return [
        { label: this.pageTitle, click: this.goToList },
        { label: this.title }
      ]
    },

    storageKey() {
      if (this.isWrongMode) {
        return `wrong_session_${this.resultId}`
      }
      return `ticket_session_${this.ticketType}_${this.ticketNumber}`
    },

    isCurrentAnswerCorrect() {
      if (!this.currentQuestion?.id) return false
      return this.isQuestionCorrectById(this.currentQuestion.id)
    },

    isCurrentAnswerWrong() {
      if (!this.currentQuestion?.id) return false
      return this.isQuestionWrongById(this.currentQuestion.id)
    },

    // Qolgan xato savollar soni (faqat wrong mode uchun)
    remainingWrongCount() {
      return this.wrongQuestionIds.length - this.resolvedWrongQuestions.length
    }
  },

  mounted() {
    this.restoreSession()
    this.restoreCurrentQuestionState()
    window.addEventListener('keydown', this.handleKeydown)
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
    if (!this.isFinished) {
      this.saveSession()
    }
  },

  watch: {
    currentIndex() {
      this.saveSession()
      this.restoreCurrentQuestionState()
    },
    selectedAnswers: {
      deep: true,
      handler() {
        this.saveSession()
      }
    }
  },

  methods: {
    getAuthHeaders() {
      const token = localStorage.getItem('accessToken') || null
      const headers = { 'Content-Type': 'application/json' }
      if (token) headers.Authorization = `Bearer ${token}`
      return headers
    },

    saveSession() {
      const payload = {
        progress: {
          currentIndex: this.currentIndex,
          selectedAnswers: this.selectedAnswers,
          showExplanation: this.showExplanation,
          isFinished: this.isFinished,
          startedAt: this.startedAt,
          resolvedWrongQuestions: this.resolvedWrongQuestions
        }
      }

      // Xato rejimda questionsni saqlamaymiz (allaqachon prop orqali keladi)
      if (!this.isWrongMode) {
        payload.selectedTicket = {
          title: this.title,
          ticketNumber: this.ticketNumber,
          resultId: this.resultId,
          questions: this.questions
        }
      }

      localStorage.setItem(this.storageKey, JSON.stringify(payload))
    },

    restoreSession() {
      try {
        const raw = localStorage.getItem(this.storageKey)
        if (!raw) return

        const parsed = JSON.parse(raw)
        const progress = parsed?.progress || {}

        if (typeof progress.currentIndex === 'number') {
          this.currentIndex = Math.min(
            Math.max(progress.currentIndex, 0),
            Math.max(this.questions.length - 1, 0)
          )
        }

        this.selectedAnswers = progress.selectedAnswers || {}
        this.showExplanation = !!progress.showExplanation
        this.isFinished = !!progress.isFinished
        this.startedAt = progress.startedAt || Date.now()
        this.resolvedWrongQuestions = progress.resolvedWrongQuestions || []
      } catch (error) {
        console.error('Session restore xatosi:', error)
      }
    },

    clearSession() {
      localStorage.removeItem(this.storageKey)
    },

    goBack() {
      this.clearSession()
      this.$emit('back')
    },

    goToList() {
      this.clearSession()
      this.$emit('go-list')
    },

    handleKeydown(e) {
      const map = { F1: 0, F2: 1, F3: 2, F4: 3, F5: 4, F6: 5 }

      if (map[e.key] !== undefined && !this.finishLoading && !this.isFinished) {
        e.preventDefault()
        const option = this.currentQuestion.options?.[map[e.key]]
        if (option) {
          this.selectAnswer(option)
        }
      }
    },

    getCorrectOptionId(question) {
      if (!question?.options?.length) return null
      const correctOption = question.options.find(option => option.is_correct === true)
      return correctOption ? correctOption.id : null
    },

    isQuestionAnswered(questionId) {
      return !!this.selectedAnswers[questionId]
    },

    isQuestionCorrectById(questionId) {
      const question = this.questions.find(q => q.id === questionId)
      if (!question) return false

      const selectedId = this.selectedAnswers[questionId]
      const correctId = this.getCorrectOptionId(question)

      if (!selectedId || !correctId) return false
      return Number(selectedId) === Number(correctId)
    },

    isQuestionWrongById(questionId) {
      const question = this.questions.find(q => q.id === questionId)
      if (!question) return false

      const selectedId = this.selectedAnswers[questionId]
      const correctId = this.getCorrectOptionId(question)

      if (!selectedId || !correctId) return false
      return Number(selectedId) !== Number(correctId)
    },

    selectAnswer(option) {
      if (this.finishLoading || this.isFinished || !this.currentQuestion?.id || this.isQuestionAnswered(this.currentQuestion.id)) return;

      this.errorMessage = '';
      this.selectedAnswers = {
        ...this.selectedAnswers,
        [this.currentQuestion.id]: option.id
      };
      this.showExplanation = true;
      this.saveSession();

      setTimeout(() => {
        if (this.isFinished) return;

        // Agar xato savol rejimida bo'lsa, to'g'ri javobni tekshirish
        if (this.isWrongMode && this.isQuestionCorrectById(this.currentQuestion.id)) {
          // To'g'ri javob topildi - bu xato savolni resolved qilish
          const wrongQuestionId = this.currentQuestion.wrong_question_id
          if (wrongQuestionId) {
            this.resolvedWrongQuestions.push(wrongQuestionId)
          }
        }

        if (this.allAnswered) {
          this.finishTest();
          return;
        }

        // Bilet rejimida xato javobdan keyin o'tish mumkin emas
        if (!this.isWrongMode && this.isQuestionWrongById(this.currentQuestion.id)) {
          this.errorMessage = "Iltimos, to'g'ri javobni tanlang.";
          return;
        }

        // Keyingi savolga o'tish
        if (this.currentIndex < this.questions.length - 1) {
          this.currentIndex += 1;
          this.restoreCurrentQuestionState();
          this.saveSession();
        }
      }, 350);
    },

    restoreCurrentQuestionState() {
      this.showExplanation = !!this.selectedAnswers[this.currentQuestion?.id]
    },

    getOptionClass(option) {
      const classes = [];

      const selectedId = this.selectedOptionId
      const correctId = this.getCorrectOptionId(this.currentQuestion)

      if (!selectedId) return classes;

      // Tanlangan option
      if (Number(option.id) === Number(selectedId)) {
        classes.push('is-selected');

        if (Number(selectedId) === Number(correctId)) {
          classes.push('is-correct');
        } else {
          classes.push('is-wrong');
        }
      }

      // Agar foydalanuvchi xato tanlagan bo‘lsa, to‘g‘ri javobni ham ko‘rsatamiz
      if (
        correctId &&
        Number(selectedId) !== Number(correctId) &&
        Number(option.id) === Number(correctId)
      ) {
        classes.push('is-correct');
      }

      return classes;
    },

    buildAnswersPayload() {
      return this.questions.map((question) => ({
        question_id: question.id,
        selected_option_id: this.selectedAnswers[question.id] || null
      }))
    },

    async finishTest() {
      if (this.isFinished || this.finishLoading) return

      this.finishLoading = true
      this.errorMessage = ''

      try {
        const durationSeconds = Math.floor((Date.now() - this.startedAt) / 1000)

        // Agar xato savollar testi bo'lsa, to'g'ri topilganlarini o'chirish uchun alohida API
        if (this.isWrongMode && this.resolvedWrongQuestions.length > 0) {
          // Har bir to'g'ri topilgan xato savolni o'chiramiz
          await Promise.all(
            this.resolvedWrongQuestions.map(wrongId =>
              axios.post(
                `${this.backendUrl}/v1/tests/wrong-question-resolve/${wrongId}/`,
                { selected_option_id: this.selectedAnswers[this.getQuestionIdByWrongId(wrongId)] },
                { headers: this.getAuthHeaders() }
              ).catch(err => console.log('Xato savol o\'chirishda xatolik:', err))
            )
          )
        }

        // Testni yakunlash
        const response = await axios.post(
          `${this.backendUrl}/v1/tests/finish/`,
          {
            result_id: this.resultId,
            duration_seconds: durationSeconds,
            answers: this.buildAnswersPayload()
          },
          { headers: this.getAuthHeaders() }
        )

        console.log(response.data.message)

        this.isFinished = true
        this.clearSession()
        this.$emit('finished')

        this.$router.push({
          path: '/result',
          query: { result_id: this.resultId }
        })
      } catch (error) {
        console.error('finish-test xatosi:', error)
        this.errorMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          'Testni yakunlashda xatolik yuz berdi'
      } finally {
        this.finishLoading = false
      }
    },

    getQuestionIdByWrongId(wrongId) {
      const question = this.questions.find(q => q.wrong_question_id === wrongId)
      return question?.id
    },

    goToQuestion(index) {
      if (index < 0 || index >= this.questions.length) return
      this.currentIndex = index
      this.restoreCurrentQuestionState()
      this.saveSession()
    },

    getQuestionBtnClass(index) {
      const classes = []
      const question = this.questions[index]

      if (this.currentIndex === index) {
        classes.push('active')
      }

      if (question) {
        if (this.isQuestionCorrectById(question.id)) {
          classes.push('answered-correct')
        } else if (this.isQuestionWrongById(question.id)) {
          classes.push('answered-wrong')
        }
      }

      return classes
    }
  }
}
</script>

<style scoped>
.test-room {
  padding-bottom: 90px;
}

.panel-card {
  min-height: 520px;
}

.title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.test-title {
  font-size: 17px;
  font-weight: 800;
  color: var(--text);
}

.test-subtitle {
  font-size: 12px;
  color: var(--text-soft);
  margin-top: 3px;
}

.question-title {
  font-size: 21px;
  line-height: 1.4;
  margin: 0 0 16px;
  color: var(--text);
  font-weight: 700;
  word-wrap: break-word;
}

.wrong-info {
  margin-bottom: 16px;
}

.wrong-alert {
  margin-bottom: 0;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 16px;
  word-wrap: break-word;
}

.option-btn {
  min-height: 48px;
  height: auto;
  border-radius: 12px !important;
  display: flex !important;
  justify-content: flex-start !important;
  align-items: center;
  padding: 10px 14px !important;
  text-align: left !important;
  background: var(--card-bg) !important;
  color: var(--text) !important;
  border: 1px solid var(--border) !important;
  transition: all 0.2s ease;
}

.option-btn:hover {
  background: var(--hover) !important;
}

.option-btn.is-selected {
  border-width: 2px !important;
}

.option-btn.is-correct {
  background: #52c41a !important;
  border-color: #52c41a !important;
  color: #fff !important;
}

.option-btn.is-wrong {
  background: #ff4d4f !important;
  border-color: #ff4d4f !important;
  color: #fff !important;
}

.option-hotkey {
  min-width: 38px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  margin-right: 12px;
  font-size: 12px;
  font-weight: 800;
  background: rgba(59, 130, 246, 0.16);
  border: 1px solid var(--border);
}

.option-text {
  flex: 1;
  white-space: normal;
  line-height: 1.4;
  font-size: 13.5px;
}

.image-wrap {
  width: 100%;
  min-height: 280px;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.02);
  display: flex;
  align-items: center;
  justify-content: center;
}

.question-image {
  width: 100%;
  max-height: 340px;
  object-fit: contain;
  display: block;
}

.no-image {
  color: var(--text-soft);
  font-size: 15px;
}

.explanation {
  margin-top: 16px;
  padding: 16px;
  border-radius: 14px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.03);
}

.explanation-title {
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 10px;
  color: var(--text);
}

.answer-status {
  line-height: 1.6;
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 700;
}
.success-text {
  color: #52c41a;
}

.error-text {
  color: #ff4d4f;
}

.fixed-bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--bg);
  border-top: 1px solid var(--border);
  padding: 12px 16px;
  z-index: 10;
}

.question-number-grid {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  max-width: 1200px;
  margin: 0 auto;
  overflow-x: auto;
  padding-bottom: 10px;
}

.question-btn {
  height: 30px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--card-bg);
  color: var(--text);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.question-btn:hover {
  background: var(--hover);
}

.question-btn.active {
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.25);
}

.question-btn.answered-correct {
  background: #52c41a;
  color: white;
  border-color: #52c41a;
}

.question-btn.answered-wrong {
  background: #ff4d4f;
  color: white;
  border-color: #ff4d4f;
}

.question-number-grid::-webkit-scrollbar {
  display: none;
}

.question-number-grid {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>